<script setup>
import { computed, inject, ref } from 'vue';
import { router } from '@inertiajs/vue3';
import { useOrderStore } from '@/stores/order';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
import { trans } from 'laravel-vue-i18n';
const { profile } = storeToRefs(useUserStore());
const modals = inject('modals');

const { search, convertQuote } = useOrderStore();
const { all } = storeToRefs(useOrderStore());
const toast = useToast();
const loading = ref(false);
const formatQuantity = (value) => Number(value).toFixed(2);

const roles = computed(() => isRole(profile.value));

const params = ref({
    term: '',
    page: 1,
    sort_by: null,
    per_page: 10,
    type_id: null,
    date: null,
    pharmacy_id: null,
});

const searchOrders = () => {
    loading.value = true;
    search({
        ...params.value,
    }).finally(() => {
        loading.value = false;
    });
};

const clearSearch = () => {
    params.value.term = '';
    searchOrders();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, {
        page,
        sort_by: sortBy,
        per_page: itemsPerPage,
    });
    searchOrders();
};

const downloadInvoice = (order) => {
    window.open(`/api/v1/orders/${order.hash}/invoice`, '_blank');
};

const getFormulas = (item) => {
    const formulas = [];
    item.compounds.forEach((compound) => {
        formulas.push({
            title: `${compound.name} - ${compound.products
                .map((product) => product.name)
                .join(', ')}`,
        });
    });

    return formulas;
};

const getQuantities = (item) => {
    const quantities = [];
    item.compounds.forEach((compound) => {
        quantities.push({
            title: `${compound.name} (${formatQuantity(compound.pivot.quantity)}x)`,
        });
    });

    return quantities;
};

const editOrder = (order) => {
    modals.show('OrderModal', {
        props: {
            order: order,
        }
    }).then(() => {
        searchOrders();
    });
};

const isQuote = (item) => {
    return item.type.name.toLowerCase() === 'quote';
};

import { useOptionsStore } from '@/stores/options';
import { isRole } from '../../utils/roleHelper';
const optionsStore = useOptionsStore();
const { orderTypeOptions, pharmacyOptions } = storeToRefs(optionsStore);

const getLabHash = () => {
    if (roles.value.pharmacyStaff() || roles.value.pharmacyAdmin()) {
        return profile.value?.pharmacy?.lab?.hash;
    } else if (roles.value.labStaff() || roles.value.labAdmin()) {
        if (profile.value.is_accessing_pharmacy) {
            return profile.value?.lab?.hash;
        }
    } else if (roles.value.systemAdmin() || roles.value.superAdmin()) {
        if (profile.value.is_accessing_pharmacy) {
            return profile.value?.active_lab?.hash;
        }
    }
    return null;
};

const createNewOrder = () => {
    const labHash = getLabHash();
    router.visit(`/labs/${labHash}/order`);
};

const accessingPharmacy = computed(() => {
    return profile.value?.is_accessing_pharmacy;
});

const reOrder = (order) => {
    const labHash = getLabHash();
    router.visit(`/labs/${labHash}/order?order_hash=${order.hash}`);
};

const convert = async (quote) => {
    if (quote.type.name == 'Order') {
        toast.error(trans('label.order_already_converted'));
        return;
    }

    const confirm = await modals.show('ConfirmationModal', {
        props: {
            title: trans('label.confirm_convert_quote'),
            prompt: trans('label.are_you_sure_convert_quote'),
        }
    });

    if (confirm) {
        try {
            const resp = await convertQuote(quote);
            const order = resp.data.order;
            router.visit(`/orders/${order.hash}/details`);
        } catch (error) {
            toast.error(trans('action.convert.error'));
        }   
    }
};

const showPharmacyDropdown = computed(() => {
    if (profile.value) {
        return (profile.value.is_accessing_lab && !profile.value.is_accessing_pharmacy && (roles.value.systemAdmin() || roles.value.superAdmin())) 
        || ((roles.value.labStaff() || roles.value.labAdmin()) && profile?.value?.is_accessing_pharmacy == false);
    }
    return false;
});
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{
        $t("label.management_title", { what: "Order" })
    }}</template>
    <template #actions>
        <StyledButton v-if="roles.pharmacyStaff() || roles.pharmacyAdmin() || accessingPharmacy" size="large" @click="createNewOrder">
            <FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t("label.create_new_order") }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-4 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchOrders"
            class="lg:col-span-2"
            name="search"
            :label="$t('label.search_multi_term_hint')"
            :debounce-events="['input']"
            :loading="loading"
            @click:clear="clearSearch" />
        <StyledDropdown
            v-model="params.type_id"
            clearable
            searchable
            :placeholder="$t('label.order_type')"
            :options="orderTypeOptions"
            name="order_type"
            @click:clear="searchOrders"
            @input="searchOrders" />
        <StyledDropdown
            v-if="showPharmacyDropdown"
            v-model="params.pharmacy_id"
            clearable
            searchable
            :placeholder="$t('label.pharmacy')"
            :options="pharmacyOptions"
            name="order_type"
            @click:clear="searchOrders"
            @input="searchOrders" />

        <StyledDatepicker
            v-model="params.date"
            :label="$t('label.order_date_range')"
            :range="true"
            clearable
            name="date"
            @click:clear="searchOrders"
            @input="searchOrders" />
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            {
                title: $t('label.id'),
                key: 'id',
                value: (item) => `${item.id}`,
            },
            {
                title: $t('label.pharmacy'),
                key: 'pharmacy.name',
                value: (item) => `${item.pharmacy.name}`,
            },
            {
                title: $t('label.date'),
                key: 'created_at',
                value: (item) =>
                    `${new Date(item.created_at).toLocaleString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`,
            },
            {
                title: $t('label.type'),
                key: 'type.name',
                value: (item) => `${item.type.name}`,
            },
            { title: $t('label.details_click_to_view'), key: 'quantity', width: '380px' },
            {
                title: $t('label.rx_number'),
                key: 'rx_number',
                value: (item) => `${item.rx_number ? '#' + item.rx_number : ''}`
            },
        ]"
        :actions="[
            {
                title: $t('label.download_invoice'),
                icon: 'mdi-download',
                action: (item) => {
                    downloadInvoice(item);
                },
            },
            {
                title: $t('label.view'),
                icon: 'mdi-eye',
                action: (item) => {
                    router.visit(`/orders/${item.hash}/details`);
                },
            },
            {
                title: $t('label.edit'),
                icon: 'mdi-pencil',
                action: (item) => {
                    editOrder(item);
                },
            },
            {
                title: $t('label.reorder'),
                icon: 'mdi-repeat',
                action: (item) => {
                    reOrder(item);
                },
            },
            {
                title: $t('label.convert_quote'),
                icon: 'mdi-swap-horizontal',
                action: (item) => {
                    convert(item)
                },
                isVisible: (item) => isQuote(item),
            }
        ]"
        @table-update="tableUpdate">
        <template #[`item.quantity`]="{ item }">
            <v-menu open-on-hover>
                <template #activator="{ props }">
                    <div class="lg:ml-8">
                        <v-btn color="primary" size="small" v-bind="props">
                            {{ $t("label.compounds") }}
                        </v-btn>
                    </div>
                </template>

                <v-list>
                    <v-list-item
                        v-for="(title, index) in getQuantities(item)"
                        :key="index"
                        :value="index">
                        <v-list-item-title>{{
                            title.title
                        }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </template>
        <template #[`item.formula`]="{ item }">
            <v-menu open-on-hover>
                <template #activator="{ props }">
                    <v-btn color="primary" size="small" v-bind="props">
                        {{ $t("label.formulas") }}
                    </v-btn>
                </template>

                <v-list>
                    <v-list-item
                        v-for="(title, index) in getFormulas(item)"
                        :key="index"
                        :value="index">
                        <v-list-item-title>{{
                            title.title
                        }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </template>
    </DataTable>
</MainContentLayout>
</template>
